/**
 * rateCard — the priced line items a subcontractor payslip is built from.
 *
 * SOURCE: "IG OPC - Contractors Payroll - 2025.xlsm", sheet PAYSLIP rows
 * 17-67 (the same 49 rows appear on NON-BIR PAYSLIP rows 16-66 at identical
 * rates). Column C is the group, D the item, G the cost, N the short code.
 * Nothing here is invented — where a rate looks odd (PREMIUM PRO 1 and 2 are
 * both ₱90; ADMIN TASKS carry no rate at all) that is what the workbook says,
 * and the payslip screen lets a rate be typed per line for exactly that case.
 *
 * THE CODE IS NOT A KEY. Column N repeats: `P1` is both PREMIUM DESIGN's ₱80
 * band and PANTS' ₱60 REGULAR 4-POCKET, and `P2`/`P3` collide the same way.
 * Keying anything on the code would price a pair of pants as a premium top —
 * a 33% overpayment that nothing downstream would question. So the key is
 * `rateKey` ("<group-slug>-<n>"), unique by construction, and the code is a
 * label the clerk recognises from the paper form.
 *
 * DESIGN CODES ARE PARSED, NOT TYPED TWICE. The item text for a sewing band
 * is a list of SS design codes ("SS 03B, 03E, SS 04"), written the way a
 * person writes a list — the `SS` prefix is dropped after the first, spacing
 * is uneven, and one band repeats a code another band already claims (076 and
 * 077 are in both E4 and E7). `designIndex` expands the text into a lookup so
 * BQC5 output can be priced automatically; the duplicate is resolved by FIRST
 * band wins, and reported through `DUPLICATE_DESIGN_CODES` rather than
 * silently, because a design paid at two different rates depending on which
 * band was consulted is a payroll dispute waiting to happen.
 *
 * Pure and I/O-free. The rows below are the SEED — the live card lives in
 * `iSyn2_SubconRateCard` (see SubconPayrollService) so a rate change is a
 * data edit and not a deploy, and the seed is what a fresh list is filled
 * with and what the app falls back to when the list is unreadable.
 */

/** Which part of the payslip a line belongs to. */
export type RateGroup =
  | "CLASSIC DESIGN"
  | "PREMIUM DESIGN"
  | "PREMIUM PRO"
  | "ELITE DESIGN"
  | "ELITE PRO DESIGN"
  | "PANTS"
  | "CUTTING"
  | "GOWNS & COATS"
  | "ADMIN TASKS";

/**
 * How a line is matched to production output.
 *
 *   design  the item text is a list of SS design codes — BQC5 output priced
 *           automatically by matching the WIP's design.
 *   manual  the item text is a description ("CARGO 6-POCKET (2-in) w/ ZIP
 *           PLY", "INVENTORY MANAGEMENT"). Real work with a real rate, but
 *           nothing in the WIP ledger names it, so the quantity is typed.
 */
export type RateMatch = "design" | "manual";

/**
 * WHICH GARMENT A BAND PAYS FOR — the one fact the work table stopped saying
 * when the GROUP column came off the paper.
 *
 * Every set is paid TWICE, a design band for the top and a PANTS band for the
 * trousers (README §2), so a payout shows the same design on two rows. On
 * 215-9/11/2026 SS032 appeared on row 1 at 110.00 and again on row 5 at
 * 110.00 — same design, same rate, two rows, and nothing on the sheet to say
 * why (Mikko, 2026-09-22: "still there's confusion here in same ITEM but
 * different RATE ... they are same RATE"). The rows were right: 24 tops and 24
 * pants, 48 quantity, which is exactly what the sheet totals. Only the reason
 * was missing.
 *
 * "" for the groups where the distinction does not apply — CUTTING and ADMIN
 * TASKS are not garments, and GOWNS & COATS are whole ones.
 */
export function bandGarment(group: string | null | undefined): string {
  const g = String(group ?? "").toUpperCase().trim();
  if (g === "PANTS") return "Pants";
  return g.endsWith("DESIGN") || g === "PREMIUM PRO" ? "Top" : "";
}

export interface IRateRow {
  /** Unique by construction — see the header on why the code is not. */
  rateKey: string;
  /** The workbook's own short code (column N). Repeats across groups. */
  code: string;
  group: RateGroup;
  /** Column D, verbatim apart from trimming. */
  item: string;
  /** Column G. Null for the ADMIN TASKS rows, which the workbook prices per
   *  engagement rather than per unit. */
  rate: number | null;
  match: RateMatch;
  /** Expanded SS design codes for a `design` row; empty otherwise. */
  designs: readonly string[];
  /** Position on the printed form, so the app's payslip reads like the paper. */
  sortOrder: number;
}

/**
 * Expand a workbook item cell into bare design codes.
 *
 * "SS 03B, 03E, SS 04" -> ["03B", "03E", "04"]. Codes are kept exactly as
 * written apart from case and surrounding space: `029` and `29` are different
 * strings in the grid too, and normalising one into the other here would
 * quietly merge two bands.
 */
export function parseDesignCodes(item: string): string[] {
  if (!/\bSS\b/i.test(item)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of item.split(",")) {
    // Each part is either "SS 03B" or a bare "03E"; a stray "SS" alone is not
    // a design and is dropped rather than becoming an empty code.
    const token = part.replace(/\bSS\b/gi, " ").trim().toUpperCase();
    if (!token || !/^[0-9]/.test(token)) continue;
    // A part like "13 ,14" survives the comma split with inner space; take
    // every whitespace-separated code inside it.
    for (const code of token.split(/\s+/)) {
      const c = code.trim();
      if (!c || !/^[0-9]/.test(c) || seen.has(c)) continue;
      seen.add(c);
      out.push(c);
    }
  }
  return out;
}

const slug = (group: RateGroup): string =>
  group.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/**
 * The workbook rows, in printed order: [group, item, rate, code, designs?].
 *
 * The fifth element names the design codes a row prices when the item text
 * does not spell them out the SS way — the gowns and coats, whose SKUs begin
 * with the code itself ("DC01-…", "DC05L-…"). Without it those rows were
 * "manual" and every doctor's coat QC'd landed under "no rate band covers
 * this design" (Mikko, 2026-09-12).
 */
const SEED: readonly [RateGroup, string, number | null, string, (readonly string[])?][] = [
  ["CLASSIC DESIGN", "SS 01A, 01B, 01C, 01D, 01I", 65, "C1"],
  ["CLASSIC DESIGN", "SS 02, SS 03, 03A, 03C, 03D, 04A, 04B, 04C, 04D, 04E, 04I, 03I", 70, "C2"],
  ["CLASSIC DESIGN", "SS 03B, 03E, SS 04", 75, "C3"],
  ["PREMIUM DESIGN", "SS 03F, 05, 05C, 06, 06A, 07, 07A, 08, 08A, 08B, 26", 80, "P1"],
  ["PREMIUM DESIGN", "SS 06B, 10, 10A, 11, 12, 13, 14, 16, SS 18, 18A", 85, "P2"],
  ["PREMIUM DESIGN", "SS 06C, SS 09, 09A, 09B, 09C", 90, "P3"],
  ["PREMIUM PRO", "SS 03H, 03G, 03J, 04H, 04G, 04J, SS 15, 15A, 15B", 90, "PP1"],
  ["PREMIUM PRO", "SS 17, 20, 20A, 21, 22, 24, 24A, 25, 27", 90, "PP2"],
  ["PREMIUM PRO", "SS 23, 19, 19A, 19B", 95, "PP3"],
  ["PREMIUM PRO", "SS 09D, 09E", 110, "PP4"],
  ["ELITE DESIGN", "SS 029, 032", 110, "E1"],
  ["ELITE DESIGN", "SS 035, 036, 037, 039, 054", 115, "E2"],
  ["ELITE DESIGN", "SS 028, 030, 031, 031A, 031B, 033, 043, 045, 048, 049, 052", 120, "E3"],
  ["ELITE DESIGN", "SS 038, 042, 050, 051, 053, 059, 076, 077", 130, "E4"],
  ["ELITE DESIGN", "SS 034, 040, 041, 044, 055, 056, 057, 058", 140, "E5"],
  ["ELITE DESIGN", "SS 064, 062, 061, 063, 094, 095", 150, "E6"],
  ["ELITE DESIGN", "SS 046, 047, 076, 077, 074, 075, 093, 096, 090, 071, 089", 160, "E7"],
  ["ELITE PRO DESIGN", "SS 080, 081, 082, 083, 084, 085, 086, 087, 070, 088", 170, "EP1"],
  ["ELITE PRO DESIGN", "SS 072, 073", 175, "EP2"],
  ["PANTS", "REGULAR 4-POCKET", 60, "P1"],
  ["PANTS", "JOGGER 4-POCKET, MATERNITY", 65, "P2"],
  ["PANTS", "CARGO 6-POCKET", 70, "P3"],
  ["PANTS", "CARGO 6-POCKET (1-in) w/ PIPING / JOGGER", 75, "P4"],
  ["PANTS", "CARGO 6-POCKET (2-in) w/ ZIP PLY", 100, "P5"],
  ["PANTS", "CARGO 6-POCKET (2-in) w/ ZIPPER or DOUBLE WELT", 110, "P6"],
  ["PANTS", "CARGO/JOGGER 6-POCKET (2-in) w/ ZIPPER", 120, "P7"],
  ["PANTS", "CARGO/JOGGER 6-P w/ INVISIBLE / TAPERED / LAP", 130, "P8"],
  ["PANTS", "ALTA CARGO/PANTS STRECTHFINE A & B, E", 150, "P9"],
  ["PANTS", "ALTA CHINO PACKET BUTTON/VNECK INVERTED THREAD/OVERLAP", 160, "P10"],
  ["PANTS", "ALTA ELITE PRO EMPATHETIC/SHOULDER SIDE PIPING", 165, "P11"],
  ["PANTS", "ALTA CARGO/ SHORT", 170, "P12"],
  ["PANTS", "ALTA OVER ALL", 175, "P13"],
  ["CUTTING", "CUT TOP AND PANTS (CLASSIC REGULAR SET)", 15, "CC1"],
  ["CUTTING", "CUT TOP AND PANTS (CLASSIC CARGO SET)", 20, "CC2"],
  ["CUTTING", "CUT TOP AND PANTS (PREMIUM PRO CARGO SET)", 25, "CP1"],
  ["CUTTING", "CUT TOP AND PANTS (STRETCHFINE) / DC 1, 2, 3", 40, "CS1"],
  // The PAYSLIP sheet's rows 53-61 (no group label of their own): medical
  // wear, priced whole — a gown has no pants line (pantsMap.NO_PANTS).
  ["GOWNS & COATS", "LAB GOWN", 400, "LG", ["LG"]],
  ["GOWNS & COATS", "NURSE UNIFORM SET", 500, "NU", ["NU", "WU01", "WU02", "WU03", "WU04", "WU05"]],
  ["GOWNS & COATS", "LONG GOWN DC03 (Physician)", 650, "DC03", ["DC03"]],
  ["GOWNS & COATS", "INTERN GOWN DC02", 450, "DC02", ["DC02"]],
  ["GOWNS & COATS", "DC04 W/FLAP", 580, "DC04", ["DC04"]],
  ["GOWNS & COATS", "DC05 LONG", 680, "DC05L", ["DC05L"]],
  ["GOWNS & COATS", "DC05 SHORT", 600, "DC05S", ["DC05S"]],
  ["GOWNS & COATS", "DC06 JACKET", 650, "DC06J", ["DC06J", "DC06"]],
  ["GOWNS & COATS", "DOCTOR'S or WHITE COAT DC01 (Resident)", 550, "DC01", ["DC01"]],
  ["ADMIN TASKS", "SALES & MARKETING/PROJECT TASK", null, "AT1"],
  ["ADMIN TASKS", "CUSTOMER SUPPORT ADMIN TASK", null, "AT2"],
  ["ADMIN TASKS", "OPERATIONS/FULFILLMENT/PREP ADMIN TASK", null, "AT3"],
  ["ADMIN TASKS", "ACCOUNTING/COMPLIANCE TASK", null, "AT4"],
  ["ADMIN TASKS", "INVENTORY MANAGEMENT", null, "AT5"],
  ["ADMIN TASKS", "TRAINING/MENTORING", null, "AT6"],
];

/**
 * The rate card as the workbook prints it.
 *
 * ADMIN TASKS CARRY NO CODE IN THE WORKBOOK — column N is blank on rows
 * 62-67. AT1-AT6 are assigned here so every line has something to say when a
 * clerk asks what a row is; nothing reads them as data.
 */
export const RATE_CARD: readonly IRateRow[] = (() => {
  const perGroup = new Map<RateGroup, number>();
  return SEED.map(([group, item, rate, code, named], i) => {
    const n = (perGroup.get(group) ?? 0) + 1;
    perGroup.set(group, n);
    const designs = named ? [...named] : parseDesignCodes(item);
    return {
      rateKey: `${slug(group)}-${n}`,
      code,
      group,
      item,
      rate,
      match: designs.length > 0 ? ("design" as const) : ("manual" as const),
      designs,
      sortOrder: i + 1,
    };
  });
})();

/** Groups in printed order, for a payslip that reads like the paper form. */
export const RATE_GROUPS: readonly RateGroup[] = (() => {
  const seen: RateGroup[] = [];
  for (const r of RATE_CARD) if (!seen.includes(r.group)) seen.push(r.group);
  return seen;
})();

/**
 * Design code -> the row that prices it. First band wins; see the header.
 *
 * Built from whatever card it is handed, so an edited live card indexes the
 * same way the seed does.
 */
export function designIndex(card: readonly IRateRow[] = RATE_CARD): Map<string, IRateRow> {
  const m = new Map<string, IRateRow>();
  for (const row of card) {
    for (const d of row.designs) if (!m.has(d)) m.set(d, row);
  }
  return m;
}

/** Design codes claimed by more than one band, with the bands that claim
 *  them — surfaced on the Rate Card screen so the overlap is a decision
 *  someone makes rather than an accident of row order. */
export function duplicateDesignCodes(
  card: readonly IRateRow[] = RATE_CARD
): { design: string; rows: IRateRow[] }[] {
  const m = new Map<string, IRateRow[]>();
  for (const row of card) {
    for (const d of row.designs) {
      const at = m.get(d);
      if (at) at.push(row);
      else m.set(d, [row]);
    }
  }
  return [...m.entries()]
    .filter(([, rows]) => rows.length > 1)
    .map(([design, rows]) => ({ design, rows }))
    .sort((a, b) => a.design.localeCompare(b.design));
}

/** The overlaps present in the shipped card, computed once. */
export const DUPLICATE_DESIGN_CODES = duplicateDesignCodes();

/**
 * The design code the grid's `design` field carries, normalised for lookup.
 *
 * The grid writes designs in several shapes — "SS 03B", "ss03b", "SS-03B",
 * and bare "03B" all mean the same band. The rate card holds the bare code,
 * so everything is reduced to that. Returns "" for a blank or unrecognisable
 * value, which prices as unmatched rather than as some arbitrary band.
 */
export function normalizeDesign(design: string | undefined | null): string {
  const s = String(design ?? "").toUpperCase().trim();
  if (!s) return "";
  const m = s.match(/^(?:SS)?[\s._-]*([0-9]{1,3}[A-Z]?)$/);
  if (m) return m[1];
  // The gowns and coats carry their own prefix — DC01, DC05L, DC06J — and
  // the two-letter codes LG and NU; those are the design as written.
  const other = s.replace(/[\s._-]+/g, "");
  return /^(?!SS)[A-Z]{2}[0-9]{2,3}[A-Z]?$/.test(other) || /^[A-Z]{2,3}$/.test(other) ? other : "";
}

/** Price one design, or null when the card has no band for it. */
export function rateForDesign(
  design: string | undefined | null,
  index: Map<string, IRateRow> = designIndex()
): IRateRow | null {
  const code = normalizeDesign(design);
  if (!code) return null;
  // "029" and "29" are written both ways in the grid and mean one band, so a
  // miss retries without leading zeros before giving up. Deliberately only in
  // this direction: the card's own codes stay exactly as the workbook wrote
  // them (see parseDesignCodes).
  return index.get(code) ?? index.get(code.replace(/^0+/, "")) ?? index.get(code.padStart(3, "0")) ?? null;
}

/**
 * How a parent SKU is written on a payslip: "028" -> "SS028".
 *
 * No space, matching how the codes are said out loud and written on a work
 * order. The rate card stores the bare code (see parseDesignCodes) precisely
 * so the prefix is a display decision made in one place.
 */
export function skuLabel(design: string): string {
  const code = String(design ?? "").toUpperCase().trim();
  return code ? `SS${code}` : "";
}

/** The SKUs actually sewn, as one cell of a payslip. */
export function skuList(designs: readonly string[] | undefined): string {
  return (designs ?? []).map(skuLabel).filter(Boolean).join(", ");
}
